import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Columns3, GripVertical, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

export interface ColumnOrderMenuColumn {
  key: string;
  label: ReactNode;
  ariaLabel?: string;
}

interface ColumnOrderMenuProps {
  columns: readonly ColumnOrderMenuColumn[];
  onMoveColumn: (key: string, direction: 'left' | 'right') => void;
  onReset: () => void;
  className?: string;
}

export function ColumnOrderMenu({
  columns,
  onMoveColumn,
  onReset,
  className,
}: ColumnOrderMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (columns.length <= 1) return null;

  return (
    <div ref={containerRef} className={cn('relative inline-flex', className)}>
      <Button
        type="button"
        variant="outline"
        className="h-9 gap-2 rounded-full px-3"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <Columns3 className="h-4 w-4" />
        {t('tableColumns.button')}
      </Button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-2 w-80 rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-lg">
          <div className="mb-3">
            <div className="text-sm font-semibold">{t('tableColumns.title')}</div>
            <p className="mt-1 text-xs text-muted-foreground">{t('tableColumns.description')}</p>
          </div>
          <div className="space-y-1">
            {columns.map((column, index) => {
              const label =
                column.ariaLabel ?? (typeof column.label === 'string' ? column.label : column.key);
              return (
                <div
                  key={column.key}
                  className="flex items-center gap-2 rounded-lg border border-border bg-card px-2 py-1.5"
                >
                  <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-sm">{column.label}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    disabled={index === 0}
                    onClick={() => onMoveColumn(column.key, 'left')}
                    aria-label={t('tableColumns.moveLeft', { column: label })}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    disabled={index === columns.length - 1}
                    onClick={() => onMoveColumn(column.key, 'right')}
                    aria-label={t('tableColumns.moveRight', { column: label })}
                  >
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex justify-end">
            <Button type="button" variant="ghost" className="h-8 gap-2 px-2" onClick={onReset}>
              <RotateCcw className="h-3.5 w-3.5" />
              {t('tableColumns.reset')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
